import BodyText from 'components/Luxkit/Typography/BodyText'
import { rem } from 'polished'
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable/Clickable'
import cn from 'clsx'
import { BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { nonNullable } from 'lib/array/arrayUtils'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import OfferCalendarDurationSelect from 'components/OfferPage/Filters/CalendarPicker/CalendarDuration/OfferCalendarDurationSelect'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import Image from 'components/Common/Image'

const Container = styled.div`
  padding: ${rem(12)} ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  width: 100%;

  ${mediaQueryUp.tablet} {
    width: ${rem(300)};
  }

  &.bundle-and-save-v2 {
    padding: ${rem(16)};

    &.active {
      border: 2px solid ${({ theme }) => theme.palette.neutral.default.one};
    }

    ${mediaQueryUp.tablet} {
      padding: ${rem(8)};
      width: auto;
    }
  }
`

const ClickableSelect = styled(Clickable)`
  --border-colour: ${({ theme }) => theme.palette.neutral.default.five};
  --bg-colour: ${({ theme }) => theme.palette.neutral.default.eight};

  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: ${rem(8)};
  align-items: center;
  padding: ${rem(8)};
  border: 1px solid var(--border-colour);
  border-radius: ${({ theme }) => theme.borderRadius.M};
  background-color: var(--bg-colour);

  &.selected {
    --border-colour: ${({ theme }) => theme.palette.neutral.default.one};
  }

  &.error {
    --border-colour: ${({ theme }) => theme.palette.messaging.critical.normal};
    --bg-colour: ${({ theme }) => theme.palette.messaging.critical.light};
  }
`

const OfferImage = styled(Image)`
    display: block;
    width: ${rem(70)};
    height: ${rem(70)};
    object-fit: cover;

    ${mediaQueryUp.tablet} {
      width: ${rem(100)};
      height: ${rem(100)};
    }
 `

const DateSelectionChip = styled(AssistChip)`
  &.selected {
    background-color: ${props => props.theme.palette.neutral.default.one};
    border-color: ${props => props.theme.palette.neutral.default.one};
    color: ${props => props.theme.palette.neutral.default.eight};
  }
`

interface Props {
  hotelNumber: number;
  bundleOffer: BundledOfferState
  onClick: (e: MouseEvent<HTMLButtonElement>, offerId?: string) => void;
  onClickLabel: string
  isSelected: boolean
  isDatePickerOpen: boolean
  noSelections: boolean
}

export default function BundleAndSaveDateSelectionTrigger({
  hotelNumber,
  bundleOffer,
  onClick,
  onClickLabel,
  isSelected,
  isDatePickerOpen,
  noSelections,
}: Props) {
  const {
    checkInDate,
    checkOutDate,
    duration,
    offer,
  } = bundleOffer

  const [isDirty, setDirty] = useState(false)

  useEffect(() => {
    if (isDatePickerOpen) {
      setDirty(false)
    }
  }, [isDatePickerOpen])

  useEffect(() => {
    if (!bundleOffer.checkInDate && !isDatePickerOpen && !noSelections) {
      setDirty(true)
    }
  }, [bundleOffer, isDatePickerOpen, noSelections])

  const dateLabel = useMemo(() => {
    if (checkInDate && duration) {
      if (isBundleAndSaveV2Enabled()) {
        const checkOutDateDuration = checkInDate.clone().add(duration, 'days')

        if (checkInDate.month() === checkOutDateDuration.month()) {
          return `${checkInDate.format('D')} - ${checkOutDateDuration.format('D MMM YYYY')}`
        }

        if (checkInDate.year() !== checkOutDateDuration.year()) {
          return `${checkInDate.format('DD MMM YYYY')} - ${checkOutDateDuration.format('DD MMM YYYY')}`
        }

        return `${checkInDate.format('DD MMM')} - ${checkOutDateDuration.format('DD MMM YYYY')}`
      }

      return `${checkInDate.format('DD MMM')} - ${checkInDate.clone().add(duration, 'days').format(
      'DD MMM',
    )} (${duration} nights)`
    }

    if (checkInDate && checkOutDate) {
      return `${checkInDate.format('DD MMM')} - ${checkOutDate.format(
        'DD MMM',
      )} (${checkOutDate.diff(checkInDate, 'days')} nights)`
    }

    return null
  }, [checkInDate, checkOutDate, duration])

  const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    onClick(e, offer?.id)
  }, [onClick, offer])

  const locationLabel = useMemo(() => {
    return nonNullable([
      offer?.locationHeading,
      offer?.locationSubheading,
    ]).join(', ')
  }, [offer])

  return (
    <Container className={
      cn({
        'bundle-and-save-v2': isBundleAndSaveV2Enabled(),
        active: isSelected && isDatePickerOpen,
      })
    }>
      {!isBundleAndSaveV2Enabled() && <>
        <Group direction="vertical">
          <BodyText variant="small">Hotel {hotelNumber}</BodyText>
          <BodyText lineClamp={1} variant="medium" weight="bold">
            {offer?.property?.name}
          </BodyText>
        </Group>
        <ClickableSelect
        onClick={handleClick}
        className={cn({
          selected: isSelected && isDatePickerOpen,
          error: isDirty,
        })}
      >
          <LineCalendarIcon />
          <BodyText variant="medium">{dateLabel ?? onClickLabel}</BodyText>
          <DropdownChevron open={isSelected && isDatePickerOpen} />
        </ClickableSelect>
      </>}
      {isBundleAndSaveV2Enabled() &&
        <Group direction="horizontal" desktopVerticalAlign="center" gap={12} >
          <CSSBreakpoint min="desktop">
            <OfferImage id={offer?.images[0]?.id} />
          </CSSBreakpoint>
          <Group direction="vertical" gap={8} fullWidth >
            <Group
            direction="horizontal-reverse"
            tabletDirection="horizontal"
            gap={8}
            horizontalAlign="space-between"
            verticalAlign="center" >
              <CSSBreakpoint max="tablet">
                <OfferImage id={offer?.images[0]?.id} />
              </CSSBreakpoint>

              <Group direction="vertical">
                <BodyText variant="small">{locationLabel}</BodyText>
                <BodyText lineClamp={1} variant="large" weight="bold">
                  {offer?.property?.name}
                </BodyText>
              </Group>
            </Group>
            <Group direction="horizontal" gap={8}>
              <OfferCalendarDurationSelect isSelected={isSelected} offerId={offer?.id} />
              <DateSelectionChip
                variant="filled"
                size="medium"
                className={
                  cn({
                    selected: isSelected && isDatePickerOpen,
                    error: isDirty,
                  })
                }
                endIcon={<DropdownChevron open={isSelected && isDatePickerOpen} />}
                onClick={handleClick}
                >
                {dateLabel ?? onClickLabel}
              </DateSelectionChip>
            </Group>
          </Group>
        </Group>}
    </Container>
  )
}
